import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Phone, Star, CheckCircle, BarChart3, UserCheck, Clock, ClipboardList, Search, PhoneCall, ThumbsUp, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SEOHead from "@/components/seo/SEOHead";
import EqualHousingLogo from "@/components/compliance/EqualHousingLogo";
import { supabase } from "@/integrations/supabase/client";
import { trackGA4Event } from "@/lib/ga4";
import { useIsMobile } from "@/hooks/use-mobile";

import heroImage from "@/assets/hero-south-jersey-home.jpg";

const PHONE = "(856) 805-2279";
const PHONE_TEL = "tel:+18568052279";
const GOOGLE_ADS_ID = "AW-11071702370";

const TIMELINE_OPTIONS = [
  { value: "asap", label: "As soon as possible (1–3 months)" },
  { value: "3-6", label: "3–6 months" },
  { value: "6-12", label: "6–12 months" },
  { value: "researching", label: "Just researching for now" },
];

const TESTIMONIALS = [
  {
    text: "We couldn't be more grateful to have had Timothy as our realtor. His knowledge of the market and sharp negotiation skills made both the selling and buying processes smooth and successful.",
    name: "Brianna",
    town: "Williamstown",
  },
  {
    text: "I have had the pleasure of working with Prime on several deals. Everyone is very responsive and very easy to work with. I would highly recommend if you're looking to purchase or sell a home.",
    name: "Frank",
    town: "Berlin",
  },
  {
    text: "Thank you to the folks here for making the process seamless. We sold our Florence home and got multiple offers, which made our relocation to Cherry Hill that much easier. Seriously, we couldn't have done this without you.",
    name: "Beatrice",
    town: "Cherry Hill",
  },
];

const SellerLandingPage = () => {
  const isMobile = useIsMobile();
  const formRef = useRef<HTMLDivElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const [formPastView, setFormPastView] = useState(false);
  const [step, setStep] = useState<1 | 2>(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form fields
  const [address, setAddress] = useState("");
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [timeline, setTimeline] = useState("");

  // Inject Google Ads (gtag.js) for PPC tracking
  useEffect(() => {
    if (document.getElementById("gads-gtag")) return;
    const script = document.createElement("script");
    script.id = "gads-gtag";
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
    document.head.appendChild(script);
    window.dataLayer = window.dataLayer || [];
    const gtag = (...args: unknown[]) => { (window.dataLayer as unknown[]).push(args); };
    gtag("js", new Date());
    gtag("config", GOOGLE_ADS_ID);
  }, []);

  // Track when user scrolls past the hero form for mobile sticky CTA
  useEffect(() => {
    if (!isMobile) return;
    const observer = new IntersectionObserver(
      ([entry]) => setFormPastView(!entry.isIntersecting),
      { threshold: 0 }
    );
    const el = formRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [isMobile]);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => addressInputRef.current?.focus(), 600);
  };

  const handlePhoneClick = () => {
    trackGA4Event("phone_click", { category: "lead_action", label: "ppc_seller_landing" });
  };

  /** Fire Google Ads conversion on lead form submit */
  const fireGadsConversion = () => {
    const g = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
    if (typeof g === "function") {
      g("event", "conversion", {
        send_to: "AW-11071702370/NCc0CKDb1cIbEOKKs58p",
        value: 1.0,
        currency: "USD",
      });
    }
  };

  const handleStep1 = (e: React.FormEvent) => {
    e.preventDefault();
    if (!address.trim()) return;
    setStep(2);
  };

  const handleStep2 = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName.trim() || !phone.trim() || !email.trim() || !timeline) return;
    setSubmitting(true);

    try {
      // Sync to FUB
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      await fetch(
        `https://${projectId}.supabase.co/functions/v1/submit-lead`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            event_type: "valuation_request",
            lead_data: {
              name: fullName.trim(),
              email: email.trim(),
              phone: phone.trim(),
              message: `Property address: ${address.trim()} | Timeline: ${timeline}`,
              tcpa_consent: true,
              consent_timestamp: new Date().toISOString(),
            },
            metadata: {
              source: "PPC Seller Landing",
              tags: ["PPC Seller Lead", "Organic Lead"],
              property_address: address.trim(),
              sell_timeline: timeline,
            },
          }),
        }
      );

      // Insert into landing_page_leads
      const params = new URLSearchParams(window.location.search);
      await supabase.from("landing_page_leads").insert({
        landing_page_id: "00000000-0000-0000-0000-000000000002",
        name: fullName.trim(),
        email: email.trim(),
        phone: phone.trim(),
        address: address.trim(),
        message: `Timeline: ${timeline}`,
        tcpa_consent: true,
        consent_timestamp: new Date().toISOString(),
        utm_source: params.get("utm_source"),
        utm_medium: params.get("utm_medium"),
        utm_campaign: params.get("utm_campaign"),
        utm_content: params.get("utm_content"),
        utm_term: params.get("utm_term"),
      });

      trackGA4Event("generate_lead", { category: "lead_action", form_name: "ppc_seller_landing" });
      trackGA4Event("form_submit", { category: "lead_action", form_name: "ppc_seller_landing" });
      fireGadsConversion();
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  const LeadForm = () => (
    <div ref={formRef} className="bg-white rounded-lg shadow-xl p-6 md:p-8 w-full max-w-md mx-auto lg:mx-0">
      {submitted ? (
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-foreground mb-2">Your Valuation Is Being Prepared</h3>
          <p className="text-muted-foreground">A local South Jersey agent will call you within 1 business hour.</p>
          <a href={PHONE_TEL} onClick={handlePhoneClick} className="inline-block mt-4 text-[#C8102E] font-semibold hover:underline">
            Or call now: {PHONE}
          </a>
        </div>
      ) : step === 1 ? (
        <form onSubmit={handleStep1} className="space-y-4">
          <h3 className="text-lg font-bold text-foreground text-center">Step 1 of 2 — Enter Your Property Address</h3>
          <Progress value={50} className="h-2" />
          <p className="text-xs text-muted-foreground text-center">Step 1 of 2</p>
          <div>
            <Label htmlFor="address" className="sr-only">Property Address</Label>
            <Input
              ref={addressInputRef}
              id="address"
              placeholder="123 Main Street, Cherry Hill, NJ"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              required
              maxLength={300}
              className="h-14 text-base"
            />
          </div>
          <Button
            type="submit"
            className="w-full h-14 text-base font-bold bg-[#C8102E] hover:bg-[#A00D24] text-white"
          >
            GET MY FREE HOME VALUE →
          </Button>
          <p className="text-xs text-muted-foreground text-center">Takes 30 seconds. No obligation. No spam.</p>
        </form>
      ) : (
        <form onSubmit={handleStep2} className="space-y-4">
          <h3 className="text-lg font-bold text-foreground text-center">Step 2 of 2 — Where Should We Send Your Report?</h3>
          <Progress value={100} className="h-2" />
          <p className="text-xs text-muted-foreground text-center">Step 2 of 2</p>
          <div>
            <Label htmlFor="fullName" className="sr-only">Full Name</Label>
            <Input id="fullName" placeholder="Full name" value={fullName} onChange={(e) => setFullName(e.target.value)} required maxLength={100} className="h-12" />
          </div>
          <div>
            <Label htmlFor="phone" className="sr-only">Phone Number</Label>
            <Input id="phone" type="tel" placeholder="Phone number" value={phone} onChange={(e) => setPhone(e.target.value)} required maxLength={30} className="h-12" />
          </div>
          <div>
            <Label htmlFor="email" className="sr-only">Email Address</Label>
            <Input id="email" type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={255} className="h-12" />
          </div>
          <div>
            <Label htmlFor="timeline" className="sr-only">When are you planning to sell?</Label>
            <Select value={timeline} onValueChange={setTimeline} required>
              <SelectTrigger className="h-12">
                <SelectValue placeholder="When are you planning to sell?" />
              </SelectTrigger>
              <SelectContent>
                {TIMELINE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-14 text-base font-bold bg-[#C8102E] hover:bg-[#A00D24] text-white"
          >
            {submitting ? "Submitting..." : "GET MY FREE VALUATION REPORT →"}
          </Button>
          <p className="text-[10px] text-muted-foreground/70 text-center leading-tight">
            By submitting this form, you agree to be contacted by MarChris Ventures LLC dba Prime Realty Partners via call, email, and text message regarding real estate and mortgage services at the number provided, including through automated means. Msg &amp; data rates may apply. This consent is not required as a condition of purchase. Reply STOP to opt out.
          </p>
        </form>
      )}
    </div>
  );

  return (
    <>
      <SEOHead
        title="What Is My Home Worth? | Free Home Valuation"
        description="Get a free home valuation for your South Jersey property. Real comps and real data from a licensed local agent at Prime Realty Partners."
        canonicalUrl="https://www.prpsj.com/what-is-my-home-worth"
        noindex
        schema={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "What Is My Home Worth? — Free South Jersey Home Valuation",
          description: "Get a free, accurate home valuation from a licensed South Jersey agent.",
          url: "https://www.prpsj.com/what-is-my-home-worth",
          publisher: {
            "@type": "RealEstateBrokerage",
            name: "Prime Realty Partners",
            url: "https://www.prpsj.com",
          },
        }}
      />

      {/* SECTION 1 — STICKY TOP BAR */}
      <div className="sticky top-0 z-50 bg-[#C8102E] text-white">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between">
          <a href={PHONE_TEL} onClick={handlePhoneClick} className="flex items-center gap-2 font-semibold text-sm hover:underline">
            <Phone className="w-4 h-4" /> {PHONE}
          </a>
          <span className="text-xs md:text-sm font-medium bg-white/20 px-3 py-1 rounded-full">
            🏡 Free Home Valuation — No Obligation
          </span>
        </div>
      </div>

      {/* SECTION 2 — HERO */}
      <section className="relative min-h-[600px] md:min-h-[700px] flex items-center">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Beautiful South Jersey colonial home exterior" width={1920} height={1080} className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-black/45" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 py-12 md:py-20 w-full">
          <div className="flex flex-col lg:flex-row lg:items-center lg:gap-12">
            <div className="flex-1 mb-8 lg:mb-0">
              <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-4">
                What Is Your South Jersey Home Worth Right Now?
              </h1>
              <p className="text-lg md:text-xl text-white/90 max-w-lg">
                Get a FREE, accurate home valuation from a local South Jersey expert — not a Zillow estimate. Real comps. Real data. Delivered in 24 hours.
              </p>
            </div>
            <div className="flex-shrink-0 lg:w-[400px]">
              <LeadForm />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3 — MARKET URGENCY STRIP */}
      <section className="bg-[#C8102E] py-8">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-xl md:text-2xl font-bold text-white text-center mb-6">
            South Jersey Is a Seller's Market. Are You Leaving Money on the Table?
          </h2>
          <div className="grid md:grid-cols-3 gap-6 text-center">
            {[
              { emoji: "📈", stat: "Home Values Up Year-Over-Year" },
              { emoji: "⚡", stat: "Homes Selling in 18–25 Days" },
              { emoji: "💰", stat: "Sellers Averaging 98%+ of List Price" },
            ].map((item, i) => (
              <div key={i} className="text-white">
                <span className="text-3xl block mb-2">{item.emoji}</span>
                <p className="font-semibold text-lg">{item.stat}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — WHY OUR VALUATION IS DIFFERENT */}
      <section className="py-16 bg-background">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-10">
            This Isn't a Zillow Zestimate. It's a Real CMA.
          </h2>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              {
                icon: BarChart3,
                title: "Local Market Data",
                desc: "We use real sold comps from Bright MLS, not national averages. Your neighborhood matters.",
              },
              {
                icon: UserCheck,
                title: "Prepared by a Licensed Agent",
                desc: "A real South Jersey expert reviews your property — not an algorithm.",
              },
              {
                icon: Clock,
                title: "Delivered in 24 Hours",
                desc: "Get a detailed report with your home's true market value by tomorrow.",
              },
            ].map((col, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-[#C8102E]/10 flex items-center justify-center mb-4">
                  <col.icon className="w-8 h-8 text-[#C8102E]" />
                </div>
                <h3 className="font-bold text-foreground mb-2">{col.title}</h3>
                <p className="text-muted-foreground text-sm">{col.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — HOW IT WORKS */}
      <section className="py-16 bg-muted">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-10">
            3 Steps to Know What Your Home Is Worth
          </h2>
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {[
              {
                icon: ClipboardList,
                title: "Submit Your Address",
                desc: "Takes 30 seconds. No obligation.",
              },
              {
                icon: Search,
                title: "We Run the Comps",
                desc: "A local agent analyzes your home against recent sold data in your neighborhood.",
              },
              {
                icon: CheckCircle,
                title: "You Get a Real Number",
                desc: "We call you with your valuation and answer any questions — no pressure, ever.",
              },
            ].map((s, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-[#C8102E]/10 flex items-center justify-center mb-4">
                  <s.icon className="w-8 h-8 text-[#C8102E]" />
                </div>
                <div className="text-sm font-bold text-[#C8102E] mb-1">Step {i + 1}</div>
                <h3 className="font-bold text-foreground mb-2">{s.title}</h3>
                <p className="text-muted-foreground text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — TESTIMONIALS */}
      <section className="py-16 bg-background">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-10">
            South Jersey Homeowners Trust Prime Realty Partners
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-card rounded-lg shadow-md border p-6">
                <div className="flex items-center gap-0.5 mb-3">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <blockquote className="text-sm text-muted-foreground italic leading-relaxed mb-3">
                  "{t.text}"
                </blockquote>
                <cite className="text-sm font-semibold text-foreground not-italic">— {t.name}, {t.town}</cite>
              </div>
            ))}
          </div>
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              {[1, 2, 3, 4, 5].map((s) => (
                <Star key={s} className="w-5 h-5 text-yellow-500 fill-yellow-500" />
              ))}
            </div>
            <p className="text-sm text-muted-foreground font-semibold">Google Reviews — 4.6/5 Stars</p>
          </div>
        </div>
      </section>

      {/* SECTION 7 — WHAT HAPPENS NEXT */}
      <section className="py-16 bg-muted">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-10">
            What Happens After You Submit?
          </h2>
          <div className={`flex ${isMobile ? "flex-col gap-6" : "flex-row items-start justify-between gap-4"}`}>
            {[
              { num: "①", icon: ClipboardList, text: "You submit your address (right now)" },
              { num: "②", icon: UserCheck, text: "An agent reviews your home info (within 1 hour)" },
              { num: "③", icon: Search, text: "We run comps from recent sold homes in your area" },
              { num: "④", icon: PhoneCall, text: "We call you with your home's real market value (within 24 hours)" },
              { num: "⑤", icon: ThumbsUp, text: "You decide what to do next — zero pressure" },
            ].map((item, i) => (
              <div key={i} className={`flex ${isMobile ? "flex-row items-start gap-4" : "flex-col items-center text-center"} flex-1`}>
                <div className="w-12 h-12 rounded-full bg-[#C8102E]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-6 h-6 text-[#C8102E]" />
                </div>
                <div>
                  <span className="text-sm font-bold text-[#C8102E]">{item.num}</span>
                  <p className="text-sm text-foreground font-medium mt-1">{item.text}</p>
                </div>
                {!isMobile && i < 4 && (
                  <ArrowRight className="w-5 h-5 text-muted-foreground mt-4 flex-shrink-0" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 8 — SECOND CTA */}
      <section className="py-16 bg-[#C8102E]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Ready to Know What Your Home Is Worth?</h2>
          <p className="text-white/90 mb-6">Sellers who know their value sell faster and for more money. Get yours free today.</p>
          <Button onClick={scrollToForm} className="h-12 px-10 bg-white text-[#C8102E] hover:bg-white/90 font-bold text-base">
            GET MY FREE HOME VALUATION →
          </Button>
          <div className="mt-4">
            <a href={PHONE_TEL} onClick={handlePhoneClick} className="text-white font-semibold hover:underline inline-flex items-center gap-2">
              <Phone className="w-4 h-4" /> {PHONE} — Call or Text a South Jersey Agent Now
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 9 — MINIMAL FOOTER */}
      <footer className="bg-foreground text-white py-10">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-lg font-bold mb-2">Prime Realty Partners</p>
          <p className="text-sm text-white/70 mb-1">31 Walnut Ave, Bellmawr, NJ 08031</p>
          <p className="text-sm text-white/70 mb-1">
            <a href={PHONE_TEL} onClick={handlePhoneClick} className="hover:underline">{PHONE}</a> · <a href="mailto:info@prpsj.com" className="hover:underline">info@prpsj.com</a>
          </p>
          <div className="flex items-center justify-center gap-4 mt-4 mb-4">
            <Link to="/privacy" className="text-xs text-white/50 hover:underline">Privacy Policy</Link>
            <Link to="/terms" className="text-xs text-white/50 hover:underline">Terms of Service</Link>
          </div>
          <div className="flex items-center justify-center gap-2 mb-3">
            <EqualHousingLogo size="sm" className="text-white/50" />
            <span className="text-xs text-white/50">Licensed Real Estate Broker · Member of Bright MLS</span>
          </div>
        </div>
      </footer>

      {/* MOBILE STICKY CTA */}
      {isMobile && formPastView && !submitted && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-3 bg-[#C8102E] shadow-2xl">
          <Button onClick={scrollToForm} className="w-full h-12 bg-white text-[#C8102E] hover:bg-white/90 font-bold text-base">
            What's My Home Worth? Get Free Value →
          </Button>
        </div>
      )}
    </>
  );
};

export default SellerLandingPage;
